import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { INDIAN_STATES } from "@/lib/indianStates";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/dateTime";
import { ArrowLeft, AlertTriangle, Check, CheckCircle2, Plus, RefreshCw, User, X } from "lucide-react";

const SECTIONS = ["Prospect", "Company", "Source & campaign", "Requirement", "Priority & owner"];
const COMPANY_SIZES = ["1 – 10", "11 – 50", "50 – 200", "200 – 500", "500+"];
const REGIONS = ["North", "South", "East", "West", "Central", "North-East"];
const TIMELINES = ["Immediate (1–2 weeks)", "Within 1 month", "Within 3 months", "Within 6 months", "Just exploring"];
const PRIORITIES = [
  { value: "HOT", label: "Hot", on: "text-warning", badge: "bg-warning-soft text-warning" },
  { value: "WARM", label: "Warm", on: "text-primary", badge: "bg-primary/10 text-primary" },
  { value: "COLD", label: "Cold", on: "text-foreground", badge: "bg-muted text-foreground" },
] as const;
/** The form fields each section holds, for the "n of m filled" count beside the section list */
const SECTION_FIELDS: string[][] = [
  ["first_name", "last_name", "job_title", "mobile", "phone", "alternate_phone", "email"],
  ["company_name", "website", "industry", "company_size", "city", "state", "region"],
  ["source", "campaign", "referred_by"],
  ["product_interest", "expected_budget", "expected_timeline", "requirement_notes"],
  ["priority", "owner_id", "next_follow_up_at"],
];
const filled = (v: unknown) => (typeof v === "number" ? v > 0 : Boolean(String(v ?? "").trim()));

/** A stored Indian mobile ("+91 98765 43211", "919876543211") as the box after the fixed +91 shows it */
const mobileBox = (stored?: string | null) => {
  const v = String(stored || "").trim();
  return /^\+?91[\s-]?[6-9]/.test(v) && v.replace(/\D/g, "").length === 12 ? v.replace(/^\+?91[\s-]?/, "") : v;
};
const selectClass = "h-9 w-full rounded-md border border-input bg-card px-2.5 text-[13px] text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60";
const inputClass = "h-9 text-[13px]";

/** A labelled field; `required` adds the red star and tells screen readers */
function Field({ label, htmlFor, required, hint, className, children }: { label: string; htmlFor: string; required?: boolean; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <Label htmlFor={htmlFor} className="text-[12px] font-medium text-foreground/80">
        <span>
          {label}
          {required && <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>}
        </span>
      </Label>
      {children}
      {hint && <p className="text-[11.5px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** One band of the form card: the numbered title and a line of help on the left, the fields on the right */
function Section({ n, title, help, active, children }: { n: number; title: string; help: string; active: boolean; children: React.ReactNode }) {
  return (
    <section id={`lead-s${n}`} aria-labelledby={`lead-h${n}`} className="grid scroll-mt-4 gap-x-8 gap-y-3 px-5 py-5 sm:px-6 lg:grid-cols-[200px_minmax(0,1fr)]">
      <div className="space-y-1">
        <h2 id={`lead-h${n}`} className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <span aria-hidden="true" className={cn("flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors", active ? "bg-primary text-primary-foreground" : "bg-sidebar text-white")}>{n}</span>
          {title}
        </h2>
        <p className="text-[12.5px] text-muted-foreground">{help}</p>
      </div>
      {children}
    </section>
  );
}

/** The fields of a section: as many equal columns as fit, so fields line up from one section to the next */
const FieldGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid content-start gap-x-4 gap-y-3.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>{children}</div>
);

/** A select over a fixed list that still shows a value the lead already has when it isn't in the list */
function ListSelect({ id, value, onChange, options, placeholder }: { id: string; value: string; onChange: (v: string) => void; options: string[]; placeholder: string }) {
  const all = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <SearchableSelect id={id} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      <option value="">{placeholder}</option>
      {all.map((o) => <option key={o} value={o}>{o}</option>)}
    </SearchableSelect>
  );
}

interface MasterLookupRecord {
  code: string;
  name: string;
}

interface AssignableUser {
  id: string;
  first_name: string;
  last_name?: string;
  email: string;
  user_role?: { name: string; code?: string };
}

export function LeadFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission, user, getScope } = useAuth();
  // Only a Super Admin routes leads company-wide; anyone else's unassigned new lead becomes their own
  const routesCompanyWide = getScope("lead") === "all";

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [dupWarning, setDupWarning] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [showOther, setShowOther] = useState(false);
  // Who a lead with no owner would go to, when the company routes by turns (only for a new lead)
  const [nextOwner, setNextOwner] = useState<{ auto_assign: boolean; next_owner: { id: string; name: string } | null } | null>(null);
  const [activeStep, setActiveStep] = useState(1);
  const [sourceMissing, setSourceMissing] = useState(false);

  // Lookups and assignable users
  const [assignableReps, setAssignableReps] = useState<AssignableUser[]>([]);
  const [masterSources, setMasterSources] = useState<MasterLookupRecord[]>([]);
  const [masterIndustries, setMasterIndustries] = useState<MasterLookupRecord[]>([]);

  // Form State
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    alternate_phone: "",
    job_title: "",
    company_name: "",
    industry: "",
    company_size: "",
    website: "",
    city: "",
    state: "",
    region: "",
    source: "",
    campaign: "",
    referred_by: "",
    product_interest: "",
    expected_budget: 0,
    expected_timeline: "",
    requirement_notes: "",
    priority: "COLD" as "HOT" | "WARM" | "COLD",
    owner_id: "",
    estimated_value: 0,
    next_follow_up_at: "",
  });

  // Load supporting data
  useEffect(() => {
    // 0. The round-robin suggestion
    if (!isEdit) {
      api
        .get("/api/Lead/NextOwner")
        .then((res) => setNextOwner(unwrapObject<any>(res)))
        .catch(() => {});
    }
    // 1. Assignable users
    api
      .get("/api/User/Assignable?for=lead")
      .then((res) => setAssignableReps(unwrapArray(res)))
      .catch(() => {});

    // 2. Sources
    api
      .get("/api/MasterLookup/ByCategory/LEAD_SOURCE")
      .then((res) => {
        const items = unwrapArray<MasterLookupRecord>(res);
        setMasterSources(
          items.length > 0
            ? items
            : [
                { code: "WEBSITE", name: "Inbound Website" },
                { code: "REFERRAL", name: "Client Referral" },
                { code: "COLD_CALL", name: "Outbound Calling" },
                { code: "CAMPAIGN", name: "Marketing Campaign" },
                { code: "EXHIBITION", name: "Trade Exhibition" },
                { code: "WHATSAPP", name: "WhatsApp Business" },
              ]
        );
      })
      .catch(() => {});

    // 3. Industries
    api
      .get("/api/MasterLookup/ByCategory/INDUSTRY")
      .then((res) => {
        const items = unwrapArray<MasterLookupRecord>(res);
        setMasterIndustries(
          items.length > 0
            ? items
            : [
                { code: "TEXTILES", name: "Textiles & Apparel" },
                { code: "MANUFACTURING", name: "Manufacturing & Industrial" },
                { code: "TECHNOLOGY", name: "Information Technology" },
                { code: "HEALTHCARE", name: "Healthcare & Pharma" },
                { code: "FINANCE", name: "Financial Services" },
                { code: "RETAIL", name: "Retail & E-Commerce" },
                { code: "LOGISTICS", name: "Logistics & Supply Chain" },
              ]
        );
      })
      .catch(() => {});
  }, []);

  // If in edit mode, fetch the lead record
  useEffect(() => {
    if (!id) return;
    setLoading(true);

    api
      .get(`/api/Lead/ById/${id}`)
      .then((res) => {
        const l = unwrapObject<any>(res);
        if (!l || !l.id) {
          error("Lead Not Found", "The requested lead could not be found");
          navigate("/leads");
          return;
        }

        setShowOther(!!l.alternate_phone);
        setForm({
          first_name: l.first_name || "",
          last_name: l.last_name || "",
          email: l.email || "",
          phone: l.phone || "",
          mobile: mobileBox(l.mobile),
          alternate_phone: l.alternate_phone || "",
          job_title: l.job_title || "",
          company_name: l.company_name || "",
          industry: l.industry || "",
          company_size: l.company_size || "",
          website: l.website || "",
          city: l.city || "",
          state: l.state || "",
          region: l.region || "",
          source: l.source || "",
          campaign: l.campaign || "",
          referred_by: l.referred_by || "",
          product_interest: l.product_interest || "",
          expected_budget: Number(l.expected_budget || 0),
          expected_timeline: l.expected_timeline || "",
          requirement_notes: l.requirement_notes || "",
          priority: l.priority || "WARM",
          owner_id: l.owner_id || "",
          estimated_value: Number(l.estimated_value || 0),
          next_follow_up_at: l.next_follow_up_at ? l.next_follow_up_at.slice(0, 16) : "",
        });
      })
      .catch((err) => {
        error("Error Loading Lead", err.message || "Could not retrieve lead details");
        navigate("/leads");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Duplicate detection
  const checkDuplicatesDebounced = async (email?: string, phone?: string, company?: string, mobile?: string) => {
    if ((!email || email.length < 5) && (!phone || phone.length < 7) && (!mobile || mobile.length < 7) && (!company || company.length < 3)) {
      setDupWarning(null);
      return;
    }
    try {
      const res = await api.post("/api/Lead/CheckDuplicate", {
        email,
        phone,
        mobile,
        company_name: company,
        exclude_id: id || undefined,
      });
      const data = res?.data || res;
      if (data?.isDuplicate && data?.reasons?.length > 0) {
        setDupWarning(data.reasons.join(" • "));
      } else {
        setDupWarning(null);
      }
    } catch {
      setDupWarning(null);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    setDirty(true);
    if (field === "source" && value) setSourceMissing(false);
    if (field === "email" || field === "phone" || field === "mobile" || field === "company_name") {
      checkDuplicatesDebounced(updated.email, updated.phone, updated.company_name, updated.mobile);
    }
  };

  const handleSubmit = async (e: React.FormEvent, addAnother = false) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.first_name.trim()) {
      error("Validation Error", "Company name and contact first name are mandatory");
      return;
    }
    if (!form.source) {
      setSourceMissing(true);
      error("Choose a source", "Say where this lead came from (Source & campaign) so reports can count it");
      document.getElementById("lead-source")?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        company_name: form.company_name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        alternate_phone: form.alternate_phone.trim() || undefined,
        job_title: form.job_title.trim() || undefined,
        industry: form.industry.trim() || undefined,
        company_size: form.company_size.trim() || undefined,
        website: form.website.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        region: form.region.trim() || undefined,
        source: form.source,
        campaign: form.campaign.trim() || undefined,
        referred_by: form.referred_by.trim() || undefined,
        product_interest: form.product_interest.trim() || undefined,
        expected_budget: Number(form.expected_budget) || 0,
        expected_timeline: form.expected_timeline.trim() || undefined,
        requirement_notes: form.requirement_notes.trim() || undefined,
        priority: form.priority,
        owner_id: form.owner_id ? form.owner_id : undefined,
        estimated_value: Number(form.estimated_value) || Number(form.expected_budget) || 0,
        next_follow_up_at: form.next_follow_up_at ? new Date(form.next_follow_up_at).toISOString() : undefined,
      };

      if (isEdit && id) {
        await api.put(`/api/Lead/Update/${id}`, payload);
        success("Lead Updated", "Lead details and user assignment saved successfully");
      } else {
        await api.post("/api/Lead/Insert", payload);
        success("Lead Created", "New lead prospect created and assigned successfully");
      }

      if (addAnother && !isEdit) window.location.assign("/leads/new");
      else navigate("/leads");
    } catch (err: any) {
      error(isEdit ? "Update Failed" : "Creation Failed", err.message || "Failed to save lead");
    } finally {
      setSubmitting(false);
    }
  };

  // Highlights the step whose card is being read: the last one whose top has passed the top of the screen, and the
  // last one once the page is scrolled to the end
  useEffect(() => {
    if (loading) return;
    const scroller = (document.querySelector("main") as HTMLElement | null) ?? document.scrollingElement;
    if (!scroller) return;
    const update = () => {
      const top = scroller.getBoundingClientRect().top;
      let current = 1;
      SECTIONS.forEach((_, i) => {
        const el = document.getElementById(`lead-s${i + 1}`);
        if (el && el.getBoundingClientRect().top - top <= 120) current = i + 1;
      });
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) current = SECTIONS.length;
      setActiveStep(current);
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    return () => scroller.removeEventListener("scroll", update);
  }, [loading]);

  const goTo = (n: number) => {
    document.getElementById(`lead-s${n}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveStep(n);
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center space-y-3">
        <RefreshCw className="size-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-muted-foreground">Loading lead…</p>
      </div>
    );
  }

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFieldChange(field, e.target.value);
  const sourceName = masterSources.find((s) => s.code === form.source)?.name;
  const suggested = !isEdit && nextOwner?.next_owner ? nextOwner.next_owner : null;
  const ownerHint = form.owner_id
    ? "Goes straight to the person you pick"
    : suggested
      ? `${suggested.name} is next in the rotation. If other leads arrive first, the next person in line gets this one.`
      : routesCompanyWide || isEdit
        ? "Stays in the unassigned pool, or is routed by your rules"
        : "Assigned to you";

  const ownerRep = form.owner_id ? assignableReps.find((r) => r.id === form.owner_id) : undefined;
  const ownerLabel = form.owner_id
    ? ownerRep
      ? `${ownerRep.first_name} ${ownerRep.last_name || ""}`.trim()
      : "Selected"
    : suggested
      ? `${suggested.name} (next in turn)`
      : routesCompanyWide || isEdit
        ? "Unassigned pool"
        : "You";
  const fullName = `${form.first_name} ${form.last_name}`.trim();
  const initials = `${form.first_name.trim()[0] ?? ""}${form.last_name.trim()[0] ?? ""}`.toUpperCase();
  const priority = PRIORITIES.find((p) => p.value === form.priority);
  const filledCount = (i: number) => (SECTION_FIELDS[i] ?? []).filter((k) => filled((form as Record<string, unknown>)[k])).length;
  const required = [
    { label: "First name", done: filled(form.first_name) },
    { label: "Company name", done: filled(form.company_name) },
    { label: "Acquisition source", done: filled(form.source) },
  ];

  const unsaved = (
    <span role="status" className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
      {dirty ? (
        <>
          <span className="inline-block size-[7px] rounded-full bg-warning" aria-hidden="true" />
          Unsaved changes{sourceName ? ` · ${sourceName}` : ""}
        </>
      ) : null}
    </span>
  );
  // The submit button sits outside the form element, so it points at the form by id
  const saveActions = (
    <>
      <Button type="button" variant="outline" onClick={() => navigate("/leads")} disabled={submitting}>
        Cancel
      </Button>
      {!isEdit && (
        <Button type="button" variant="outline" disabled={submitting} onClick={(e) => handleSubmit(e as unknown as React.FormEvent, true)}>
          Save and add another
        </Button>
      )}
      <Button type="submit" form="lead-form" disabled={submitting}>
        {submitting ? (
          <span className="flex items-center gap-1.5">
            <RefreshCw className="size-4 animate-spin" />
            <span>Saving…</span>
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="size-4" />
            <span>{isEdit ? "Update lead" : "Save lead"}</span>
          </span>
        )}
      </Button>
    </>
  );

  return (
    <div className="mx-auto w-full max-w-[1360px] space-y-4 pb-2">
      {/* Title on the left, the save actions always in reach on the right */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="space-y-0.5">
          <Link to="/leads" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-3.5" />
            Leads
          </Link>
          <h1 className="text-[22px] font-semibold leading-tight tracking-tight">{isEdit ? "Edit lead" : "New lead"}</h1>
          <p className="text-[13px] text-muted-foreground">Only first name, company and source are needed. Everything else can wait.</p>
        </div>
        <div className="hidden items-center gap-2 md:flex">
          {unsaved}
          {saveActions}
        </div>
      </div>

      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1 space-y-3">
          {dupWarning && (
            <div role="alert" className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning-soft p-3 text-xs text-warning">
              <AlertTriangle className="size-4 shrink-0" />
              <div className="flex-1 font-medium">{dupWarning}</div>
            </div>
          )}

          {/* One card; the sections are divided by thin lines */}
          <form id="lead-form" onSubmit={handleSubmit} className="divide-y divide-border rounded-xl border border-border bg-card">
            <Section n={1} title="Prospect" help="The person you will talk to." active={activeStep === 1}>
              <FieldGrid>
                <Field label="First name" htmlFor="lead-first" required>
                  <Input id="lead-first" required placeholder="e.g. Rajesh" value={form.first_name} onChange={set("first_name")} className={inputClass} />
                </Field>
                <Field label="Last name" htmlFor="lead-last">
                  <Input id="lead-last" placeholder="e.g. Singhania" value={form.last_name} onChange={set("last_name")} className={inputClass} />
                </Field>
                <Field label="Job title / designation" htmlFor="lead-title">
                  <Input id="lead-title" placeholder="e.g. Managing Director" value={form.job_title} onChange={set("job_title")} className={inputClass} />
                </Field>
                <Field
                  label="Mobile"
                  htmlFor="lead-mobile"
                  hint={form.mobile.trim() && !form.mobile.trim().startsWith("+") && form.mobile.replace(/\D/g, "").length !== 10 ? "Enter the 10-digit mobile number" : undefined}
                >
                  <div className="flex">
                    {/* +91 is added when the lead is saved; typing + first lets you enter another country's number */}
                    {!form.mobile.trim().startsWith("+") && (
                      <span className="inline-flex h-9 items-center rounded-l-md border border-r-0 border-input bg-muted px-2.5 font-mono text-[13px] text-muted-foreground" aria-hidden="true">+91</span>
                    )}
                    <Input id="lead-mobile" inputMode="tel" placeholder="98765 43211" aria-label="Mobile (+91 is added)" value={form.mobile} onChange={set("mobile")} className={cn(inputClass, "font-mono", !form.mobile.trim().startsWith("+") && "rounded-l-none")} />
                  </div>
                </Field>
                <Field label="Landline" htmlFor="lead-phone">
                  <Input id="lead-phone" inputMode="tel" placeholder="0422 2345678" value={form.phone} onChange={set("phone")} className={cn(inputClass, "font-mono")} />
                </Field>
                {showOther && (
                  <Field label="Another number" htmlFor="lead-alt">
                    <div className="flex gap-1.5">
                      <Input id="lead-alt" inputMode="tel" placeholder="Mobile or landline" value={form.alternate_phone} onChange={set("alternate_phone")} className={cn(inputClass, "font-mono")} />
                      <Button type="button" variant="ghost" size="icon" aria-label="Remove this number" onClick={() => { handleFieldChange("alternate_phone", ""); setShowOther(false); }}>
                        <X className="size-4" />
                      </Button>
                    </div>
                  </Field>
                )}
                <Field label="Email address" htmlFor="lead-email">
                  <Input id="lead-email" type="email" placeholder="rajesh@company.com" value={form.email} onChange={set("email")} className={inputClass} />
                </Field>
                {!showOther && (
                  <Button type="button" variant="ghost" size="sm" className="col-span-full -mt-1 h-7 w-fit px-1.5 text-primary" onClick={() => setShowOther(true)}>
                    <Plus className="mr-1 size-3.5" />Add another number
                  </Button>
                )}
              </FieldGrid>
            </Section>

            <Section n={2} title="Company" help="Where they work and where it is." active={activeStep === 2}>
              <FieldGrid>
                <Field label="Company name" htmlFor="lead-company" required>
                  <Input id="lead-company" required placeholder="e.g. Acme Textiles Pvt Ltd" value={form.company_name} onChange={set("company_name")} className={inputClass} />
                </Field>
                <Field label="Company website" htmlFor="lead-website">
                  <Input id="lead-website" placeholder="www.company.com" value={form.website} onChange={set("website")} className={inputClass} />
                </Field>
                <Field label="Industry sector" htmlFor="lead-industry">
                  <ListSelect id="lead-industry" value={form.industry} onChange={(v) => handleFieldChange("industry", v)} options={masterIndustries.map((i) => i.name)} placeholder="Select industry" />
                </Field>
                <Field label="Company size (headcount)" htmlFor="lead-size">
                  <ListSelect id="lead-size" value={form.company_size} onChange={(v) => handleFieldChange("company_size", v)} options={COMPANY_SIZES} placeholder="Select size" />
                </Field>
                <Field label="City" htmlFor="lead-city">
                  <Input id="lead-city" placeholder="e.g. Coimbatore" value={form.city} onChange={set("city")} className={inputClass} />
                </Field>
                <Field label="State / province" htmlFor="lead-state">
                  <ListSelect id="lead-state" value={form.state} onChange={(v) => handleFieldChange("state", v)} options={INDIAN_STATES.map((s) => s.name)} placeholder="Select state" />
                </Field>
                <Field label="Region / territory" htmlFor="lead-region">
                  <ListSelect id="lead-region" value={form.region} onChange={(v) => handleFieldChange("region", v)} options={REGIONS} placeholder="Select region" />
                </Field>
              </FieldGrid>
            </Section>

            <Section n={3} title="Source & campaign" help="How they found you, for your reports." active={activeStep === 3}>
              <FieldGrid>
                <Field label="Acquisition source" htmlFor="lead-source" required>
                  <SearchableSelect
                    id="lead-source"
                    aria-required="true"
                    value={form.source}
                    onChange={(e) => handleFieldChange("source", e.target.value)}
                    aria-invalid={sourceMissing || undefined}
                    className={cn(selectClass, sourceMissing && "border-destructive ring-2 ring-destructive/25")}
                  >
                    <option value="">Select source</option>
                    {form.source && !masterSources.some((s) => s.code === form.source) && <option value={form.source}>{form.source}</option>}
                    {masterSources.map((s) => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </SearchableSelect>
                  {sourceMissing && <p role="alert" className="text-[11.5px] text-destructive">Choose where this lead came from.</p>}
                </Field>
                <Field label="Campaign identifier" htmlFor="lead-campaign" hint="Ad, mailer or event name">
                  <Input id="lead-campaign" placeholder="e.g. Cotton fabrics Q4" value={form.campaign} onChange={set("campaign")} className={inputClass} />
                </Field>
                <Field label="Referred by" htmlFor="lead-referred" hint={form.source === "REFERRAL" ? "Who sent them to you" : undefined}>
                  <Input id="lead-referred" placeholder="Name of the referrer" value={form.referred_by} onChange={set("referred_by")} className={inputClass} />
                </Field>
              </FieldGrid>
            </Section>

            <Section n={4} title="Requirement" help="What they want, how much and by when." active={activeStep === 4}>
              <FieldGrid>
                <Field label="Product or service of interest" htmlFor="lead-product">
                  <Input id="lead-product" placeholder="e.g. Cotton poplin 60s" value={form.product_interest} onChange={set("product_interest")} className={inputClass} />
                </Field>
                <Field label="Expected budget (₹)" htmlFor="lead-budget">
                  <Input id="lead-budget" type="number" min={0} inputMode="numeric" placeholder="500000" value={form.expected_budget || ""} onChange={(e) => handleFieldChange("expected_budget", Number(e.target.value))} className={cn(inputClass, "font-mono")} />
                </Field>
                <Field label="Expected purchase timeline" htmlFor="lead-timeline">
                  <ListSelect id="lead-timeline" value={form.expected_timeline} onChange={(v) => handleFieldChange("expected_timeline", v)} options={TIMELINES} placeholder="Select timeline" />
                </Field>
                <Field label="Client requirement notes" htmlFor="lead-notes" className="col-span-full">
                  <textarea
                    id="lead-notes"
                    rows={3}
                    placeholder="Specific requirements, quantities, delivery dates, pricing asked for…"
                    value={form.requirement_notes}
                    onChange={set("requirement_notes")}
                    className="w-full resize-y rounded-md border border-input bg-card px-2.5 py-2 text-[13px] outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
                  />
                </Field>
              </FieldGrid>
            </Section>

            <Section n={5} title="Priority & owner" help="Who follows up, and when." active={activeStep === 5}>
              <FieldGrid>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <span id="lead-priority-label" className="text-[12px] font-medium text-foreground/80">Priority tier</span>
                  <div role="radiogroup" aria-labelledby="lead-priority-label" className="grid h-9 grid-cols-3 rounded-md border border-input bg-muted p-[3px]">
                    {PRIORITIES.map((p) => {
                      const on = form.priority === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => handleFieldChange("priority", p.value)}
                          className={cn(
                            "rounded-[5px] text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                            on ? cn("bg-card font-semibold shadow-sm", p.on) : "font-medium text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Field label="Assign lead to sales representative" htmlFor="lead-owner">
                  <SearchableSelect
                    id="lead-owner"
                    value={form.owner_id}
                    onChange={(e) => handleFieldChange("owner_id", e.target.value)}
                    // Changing an existing lead's owner is a reassignment and needs lead.assign
                    disabled={isEdit && !hasPermission("lead.assign")}
                    className={selectClass}
                  >
                    <option value="">{suggested ? `${suggested.name} (round-robin suggestion)` : routesCompanyWide ? "Leave in the unassigned pool (or auto-route)" : "Assign to me"}</option>
                    {assignableReps.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.first_name} {r.last_name || ""} — {r.user_role?.name || "Representative"}
                      </option>
                    ))}
                  </SearchableSelect>
                </Field>
                <Field label="Next scheduled follow-up" htmlFor="lead-followup">
                  <Input id="lead-followup" type="datetime-local" value={form.next_follow_up_at} onChange={set("next_follow_up_at")} className={inputClass} />
                </Field>
                <p className="col-span-full text-[11.5px] text-muted-foreground">{ownerHint}</p>
              </FieldGrid>
            </Section>
          </form>

          {/* On phones the actions stay at the bottom of the screen, where the thumb is */}
          <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-card/95 px-1 py-2.5 backdrop-blur md:hidden">
            <div className="min-w-[140px] flex-1">{unsaved}</div>
            {saveActions}
          </div>
        </div>

        {/* What is being saved, what is still missing, and quick jumps */}
        <aside aria-label="Lead summary" className="w-full space-y-3 xl:sticky xl:top-0 xl:w-[300px] xl:shrink-0">
          <div className="space-y-3.5 rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {initials || <User className="size-5" />}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{fullName || (isEdit ? "Lead" : "New lead")}</p>
                <p className="truncate text-[12.5px] text-muted-foreground">{form.company_name.trim() || "Company not set yet"}</p>
              </div>
            </div>
            <dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 text-[12.5px]">
              <dt className="text-muted-foreground">Source</dt>
              <dd className={cn("truncate text-right", !form.source && "text-muted-foreground")}>{sourceName || form.source || "Not set"}</dd>
              <dt className="text-muted-foreground">Priority</dt>
              <dd className="text-right">
                <span className={cn("rounded px-1.5 py-px text-[11.5px] font-semibold", priority?.badge)}>{priority?.label}</span>
              </dd>
              <dt className="text-muted-foreground">Owner</dt>
              <dd className="truncate text-right">{ownerLabel}</dd>
              <dt className="text-muted-foreground">Follow-up</dt>
              <dd className={cn("truncate text-right", !form.next_follow_up_at && "text-muted-foreground")}>
                {form.next_follow_up_at ? formatDateTime(form.next_follow_up_at) : "Not scheduled"}
              </dd>
            </dl>
          </div>

          <div className="space-y-2.5 rounded-xl border border-border bg-card p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Needed to save</h2>
            <ul className="space-y-2 text-[13px]">
              {required.map((r) => (
                <li key={r.label} className={cn("flex items-center gap-2", !r.done && "text-muted-foreground")}>
                  {r.done ? (
                    <span className="flex size-4 items-center justify-center rounded-full bg-success text-white"><Check className="size-2.5" strokeWidth={3.5} /></span>
                  ) : (
                    <span className="size-4 rounded-full border-[1.5px] border-border" />
                  )}
                  {r.label}
                  <span className="sr-only">{r.done ? " (done)" : " (still needed)"}</span>
                </li>
              ))}
            </ul>
            <p className="border-t border-border pt-2.5 text-xs text-muted-foreground">
              Phone, email and company are checked against existing leads as you type, so duplicates show up before you save.
            </p>
          </div>

          <nav aria-label="Form sections" className="rounded-xl border border-border bg-card p-2">
            {SECTIONS.map((label, i) => {
              const on = activeStep === i + 1;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => goTo(i + 1)}
                  aria-current={on ? "step" : undefined}
                  className={cn(
                    "flex h-8 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                    on ? "bg-muted font-semibold text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <span>{i + 1} · {label}</span>
                  <span className="text-[11.5px] font-normal text-muted-foreground">{filledCount(i)} of {(SECTION_FIELDS[i] ?? []).length}</span>
                </button>
              );
            })}
          </nav>
        </aside>
      </div>
    </div>
  );
}
